<template>
  <v-app
    v-if="ready"
    :class="{ 'v-application--is-mobile': isMobile }">
    <headerBar
      v-if="!hideSideBar"
      v-model="drawer"
      :window-width="windowWidth"
      :warehouses="warehouses"
      :selected-warehouse="selectedWarehouse"
      @warehouse="selectWarehouse" />
    <sidebarMenu
      v-if="!hideSideBar"
      v-model="drawer"
      :brand-name="brandName"
      :is-mobile="isMobile"
      :items="menus"
      :dark-mode="darkMode"
      @logout="logout"
      @darkMode="setDarkMode" />
    <v-main>
      <v-fade-transition mode="out-in">
        <router-view />
      </v-fade-transition>
      <pageLoading />
    </v-main>
    <snackbar
      :value="snackbar.value"
      :message="snackbar.message"
      :type="snackbar.type"
      :can-close="snackbar.canClose"
      :timeout="snackbar.timeout"
      @close="closeSnackbar" />
    <modal-confirm
      :active="!isUpToDate"
      :confirm-text="'Reload'"
      :title="'New Version Found.'"
      :message="'Please click reload to update new version.'"
      :show-cancel="false"
      @onConfirm="logout"
    />
  </v-app>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import axios from 'axios'
import sidebarMenu from '@/components/sidebar-menu'
import headerBar from '@/components/header-bar'
import modalConfirm from '@/components/modal-confirm'
import snackbar from '@/components/snackbar'
import pageLoading from '@/components/page-loading.vue'
import menus from '@/utils/menu'
import { getAuth } from '@/utils/local-storage'

export default {
  name: 'App',
  components: {
    sidebarMenu,
    snackbar,
    headerBar,
    pageLoading,
    modalConfirm
  },
  data () {
    return {
      menus,
      brandName: '',
      drawer: false,
      windowWidth: 0,
      windowHeight: 0,
      isMobile: false,
      ready: false,
      isUpToDate: true
    }
  },
  computed: {
    ...mapGetters({
      warehouses: 'Warehouse/warehouse',
      selectedWarehouse: 'Warehouse/selectedWarehouse',
      snackbar: 'Style/snackbar',
      darkMode: 'Style/darkMode'
    }),
    hideSideBar () {
      if (this.$route && this.$route.meta) {
        return this.$route.meta.hideSideBar
      }
      return false
    }
  },
  watch: {
    $route () {
      this.brandName = getAuth() ? getAuth().brand.name : ''
    },
    darkMode (val) {
      this.$vuetify.theme.dark = val
    },
    isMobile (val) {
      if (val) {
        this.drawer = false
      }
    }
  },
  created () {
    this.checkVersion()
    setInterval(this.checkVersion, 10000)
  },
  async mounted () {
    this.$vuetify.theme.dark = this.darkMode
    this.closeSnackbar()
    this.$nextTick(() => {
      window.addEventListener('resize', this.getWindowSize)
      this.getWindowSize()
    })
    if (getAuth() && getAuth().brand.name) {
      this.brandName = getAuth().brand.name
    }
    this.ready = true
    this.brandName = getAuth().brand.name
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Style/setSnackbar',
      setDarkMode: 'Style/setDarkMode',
      setWarehouse: 'Warehouse/setWarehouse',
      selectWarehouse: 'Warehouse/selectWarehouse',
      clearItems: 'Order/clearItems'
    }),
    checkVersion () {
      const vm = this
      const now = Date.now()
      axios.get(`/version.txt?t=${now}`)
        .then((response) => {
          const clientScriptVersion = window.campVersion.trim()
          const serverScriptVersion = response.data.trim()
          if (clientScriptVersion === serverScriptVersion) {
            vm.isUpToDate = true
          } else {
            vm.isUpToDate = false
          }
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.log(err)
        })
    },
    getWindowSize () {
      this.windowWidth = document.documentElement.clientWidth
      this.windowHeight = document.documentElement.clientHeight
      this.isMobile = this.windowWidth < 768
    },
    closeSnackbar () {
      this.setSnackbar({ value: false })
    },
    logout () {
      this.clearItems()
      localStorage.removeItem('auth_user')
      window.location.reload()
    }
  }
}
</script>

<style lang='scss'>
$body-font-family: 'Roboto', 'TH Sarabun', sans-serif !important;

#app {
  font-family: $body-font-family;
  .display-4 {
    font-family: $body-font-family;
  }
  .display-3 {
    font-family: $body-font-family;
  }
  .display-2 {
    font-family: $body-font-family;
  }
  .display-1 {
    font-family: $body-font-family;
  }
  .headline {
    font-family: $body-font-family;
  }
  .title {
    font-family: $body-font-family;
  }
  .subtitle-1 {
    font-family: $body-font-family;
  }
  .subtitle-2 {
    font-family: $body-font-family;
  }
  .body-1 {
    font-family: $body-font-family;
  }
  .body-2 {
    font-family: $body-font-family;
  }
  .caption {
    font-family: $body-font-family;
  }
  .overline {
    font-family: $body-font-family;
  }
}
html::-webkit-scrollbar {
  display: none;
}
.theme--light.v-app-bar.v-toolbar.v-sheet {
  background-color: #ffffff;
}
.theme--light main.v-main {
  background-color: #f1f1f1;
}
.theme--dark main.v-main {
  background-color: #464646;
}
.v-application--is-mobile .main-container.container {
  height: calc(100vh - 110px);
}
.main-container.container {
  max-height: calc(100vh - 62px);
  height: calc(100vh - 62px);
  padding: 0;
}
@media screen and (min-width: 768px) {
  .main-container.container {
    padding: 20px;
  }
}
.main-card.v-card {
  height: 100%;
  margin-top: 3px;
  overflow-y: auto;
}
.form-group.d-flex>.label-input {
  margin: 0;
}
.label-input {
  display: flex;
  align-items: center;
  width: 110px;
  margin-bottom: 5px;
}
.upload input[type=file] {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  outline: none;
  cursor: pointer;
  z-index: -1;
}
.upload {
  display: inline-flex;
}
.control {
  width: 100%;
  box-sizing: border-box;
  clear: both;
  font-size: 1rem;
  position: relative;
  text-align: left;
}
.upload .upload-draggable {
  width: 100%;
  cursor: pointer;
  padding: 0.25em;
  border: 1px dashed #b5b5b5;
  border-radius: 6px;
}
.form-group {
  position: relative;
}
.error-input {
  position: absolute;
  font-size: 11px;
  bottom: -18px;
  color: red;
  right: 5px;
}
input[type=number]::-webkit-inner-spin-button,
input[type=number]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.svg-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}
.cs-pointer {
  cursor: pointer;
}
header.app-bar-mobile > .v-toolbar__content * {
  color: #ffffff;
}
header.app-bar-mobile >.v-toolbar__extension {
  background-color: #ffffff;
}
.skus-table table {
  margin-bottom: 10px;
}
.v-input input {
  font-family: "Arial" !important;
}
</style>
